import { useState } from "react";
import { DEBRIS_ITEMS, TIER1_MATERIALS, TIER2_MATERIALS, TIER3_MATERIALS } from "../data/items";
import { SMELT_RECIPES } from "../data/recipes";
import { fmt } from "../utils/fmt";

// ── Rarity helpers ────────────────────────────────────────────────────────────
const RARITY_LABEL = { 1: "COMMON", 0.9: "UNCOMMON", 0.75: "RARE", 0.4: "DROP", 0.35: "DROP", 0.15: "EXOTIC" };
const RARITY_COLOR = { 1: "#aabbcc", 0.9: "#44ddaa", 0.75: "#aa88ff", 0.4: "#ffcc44", 0.35: "#ffcc44", 0.15: "#ff55cc" };
const TIER_COLOR   = { 1: "#ff9944", 2: "#44ccff", 3: "#cc88ff" };
const TIER_LABEL   = { 1: "T1", 2: "T2", 3: "T3" };

// Pre-build a lookup: matId → recipes that use it as input
const USED_IN = {};
for (const r of SMELT_RECIPES) {
  for (const inp of r.inputs) {
    if (!USED_IN[inp.id]) USED_IN[inp.id] = [];
    USED_IN[inp.id].push(r.name);
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────
function Badge({ label, color }) {
  return (
    <span style={{
      fontSize: 7, fontWeight: 800, letterSpacing: 1,
      color, border: `1px solid ${color}55`,
      borderRadius: 3, padding: "1px 4px",
      background: `${color}11`,
    }}>{label}</span>
  );
}

function SectionHeader({ label, color, count, total }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      marginBottom: 8, marginTop: 4,
    }}>
      <div style={{ width: 3, height: 14, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, color }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${color}33, transparent)` }} />
      <span style={{ fontSize: 8, color: "rgba(255,255,255,0.3)" }}>{count}/{total}</span>
    </div>
  );
}

function ItemCard({ item, qty, badgeLabel, badgeColor, usedIn, isLocked }) {
  const [showTip, setShowTip] = useState(false);
  const hasQty = qty > 0;

  return (
    <div
      onClick={() => usedIn?.length && setShowTip(s => !s)}
      style={{
        position: "relative",
        background: hasQty
          ? `linear-gradient(135deg, rgba(${hexToRgb(item.color)},0.10) 0%, rgba(${hexToRgb(item.color)},0.04) 100%)`
          : "rgba(255,255,255,0.025)",
        border: hasQty
          ? `1px solid ${item.color}44`
          : "1px solid rgba(255,255,255,0.07)",
        borderRadius: 8,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        cursor: usedIn?.length ? "pointer" : "default",
        opacity: isLocked ? 0.35 : hasQty ? 1 : 0.55,
        transition: "border-color 0.15s",
        minWidth: 0,
      }}
    >
      {/* Top row: icon + name + badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 18, lineHeight: 1, filter: hasQty ? "none" : "grayscale(1)" }}>
          {item.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 10, fontWeight: 700,
            color: hasQty ? item.color : "rgba(255,255,255,0.3)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{item.name}</div>
          {badgeLabel && <Badge label={badgeLabel} color={badgeColor} />}
        </div>
        {isLocked && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>🔒</span>}
      </div>

      {/* Qty row */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{
          fontSize: 16, fontWeight: 800,
          color: hasQty ? "#ffffff" : "rgba(255,255,255,0.2)",
          lineHeight: 1,
        }}>{hasQty ? fmt(qty) : "0"}</span>
        {usedIn?.length > 0 && hasQty && (
          <span style={{ fontSize: 8, color: "rgba(255,255,255,0.3)" }}>
            used in {usedIn.length} recipe{usedIn.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Recipe tooltip */}
      {showTip && usedIn?.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50,
          background: "rgba(4,10,22,0.97)", border: "1px solid rgba(100,180,255,0.25)",
          borderRadius: 6, padding: "6px 8px", width: 160,
          boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
        }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: "rgba(100,180,255,0.7)", letterSpacing: 1, marginBottom: 4 }}>
            USED IN
          </div>
          {usedIn.map(name => (
            <div key={name} style={{ fontSize: 9, color: "rgba(255,255,255,0.65)", padding: "1px 0" }}>
              • {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hex to RGB helper for CSS rgba() ─────────────────────────────────────────
function hexToRgb(hex) {
  if (!hex || hex[0] !== "#") return "255,255,255";
  const h = hex.replace("#", "");
  const full = h.length === 3
    ? h.split("").map(c => c + c).join("")
    : h;
  const num = parseInt(full, 16);
  return `${(num >> 16) & 255},${(num >> 8) & 255},${num & 255}`;
}

// ── Filter bar ────────────────────────────────────────────────────────────────
const FILTERS = ["ALL", "SALVAGE", "REFINED", "OWNED"];

// ── Main tab ─────────────────────────────────────────────────────────────────
export function InventoryTab({ inventory, materials, discoveredMaterials }) {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const discovered = discoveredMaterials instanceof Set ? discoveredMaterials : new Set(discoveredMaterials ?? []);

  const query = search.toLowerCase().trim();

  // Salvage sections
  const starterItems     = DEBRIS_ITEMS.filter(d => d.category === "starter");
  const discoverableT2   = DEBRIS_ITEMS.filter(d => d.category === "discoverable" && [0.9].includes(d.rarity));
  const discoverableT3   = DEBRIS_ITEMS.filter(d => d.category === "discoverable" && [0.75].includes(d.rarity));
  const dropOnly         = DEBRIS_ITEMS.filter(d => d.category === "drop_only");

  // Refined sections
  const t1mats = TIER1_MATERIALS;
  const t2mats = TIER2_MATERIALS;
  const t3mats = TIER3_MATERIALS;

  function filterItems(items, isDebris) {
    let result = items;
    if (filter === "OWNED") result = result.filter(i => (isDebris ? inventory : materials)[i.id] > 0);
    if (filter === "SALVAGE" && !isDebris) return [];
    if (filter === "REFINED" && isDebris) return [];
    if (query) result = result.filter(i => i.name.toLowerCase().includes(query) || i.id.includes(query));
    return result;
  }

  const totalSalvageOwned = DEBRIS_ITEMS.reduce((s, d) => s + (inventory[d.id] > 0 ? 1 : 0), 0);
  const totalMatOwned     = [...TIER1_MATERIALS, ...TIER2_MATERIALS, ...TIER3_MATERIALS]
    .reduce((s, m) => s + (materials[m.id] > 0 ? 1 : 0), 0);

  const showSalvage  = filter !== "REFINED";
  const showRefined  = filter !== "SALVAGE";

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      background: "rgba(2,6,14,1)", overflow: "hidden",
      fontFamily: "'Share Tech Mono', monospace",
    }}>

      {/* ── Header ── */}
      <div style={{
        padding: "10px 14px 8px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        flexShrink: 0,
        background: "rgba(4,10,22,0.98)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>🎒</span>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 2, color: "#aaddff" }}>
            CARGO MANIFEST
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>
            {totalSalvageOwned + totalMatOwned} types held
          </span>
        </div>

        {/* Search */}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items…"
          style={{
            width: "100%", boxSizing: "border-box",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6, padding: "5px 10px",
            color: "#aaddff", fontSize: 10, fontFamily: "inherit",
            outline: "none", marginBottom: 8,
          }}
        />

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 4 }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              flex: 1, padding: "4px 0",
              background: filter === f ? "rgba(100,180,255,0.15)" : "rgba(255,255,255,0.04)",
              border: filter === f ? "1px solid rgba(100,180,255,0.4)" : "1px solid rgba(255,255,255,0.08)",
              borderRadius: 5, color: filter === f ? "#aaddff" : "rgba(255,255,255,0.35)",
              fontSize: 8, fontWeight: 800, letterSpacing: 1, cursor: "pointer",
              fontFamily: "inherit",
            }}>{f}</button>
          ))}
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px 24px" }}>

        {/* ───── SALVAGE ───── */}
        {showSalvage && (
          <>
            {/* Starter T1 */}
            {filterItems(starterItems, true).length > 0 && (
              <section style={{ marginBottom: 18 }}>
                <SectionHeader
                  label="BASE SALVAGE — T1"
                  color="#e8a44a"
                  count={starterItems.filter(i => inventory[i.id] > 0).length}
                  total={starterItems.length}
                />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                  {filterItems(starterItems, true).map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      qty={inventory[item.id] ?? 0}
                      badgeLabel={RARITY_LABEL[item.rarity]}
                      badgeColor={RARITY_COLOR[item.rarity]}
                      usedIn={USED_IN[item.id] ?? []}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Discoverable T2 */}
            {filterItems(discoverableT2, true).length > 0 && (
              <section style={{ marginBottom: 18 }}>
                <SectionHeader
                  label="RARE SALVAGE — T2"
                  color="#44ddaa"
                  count={discoverableT2.filter(i => discovered.has(i.id) || inventory[i.id] > 0).length}
                  total={discoverableT2.length}
                />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                  {filterItems(discoverableT2, true).map(item => {
                    const isLocked = !discovered.has(item.id) && !(inventory[item.id] > 0);
                    return (
                      <ItemCard
                        key={item.id}
                        item={item}
                        qty={inventory[item.id] ?? 0}
                        badgeLabel={isLocked ? "LOCKED" : RARITY_LABEL[item.rarity]}
                        badgeColor={isLocked ? "rgba(255,255,255,0.3)" : RARITY_COLOR[item.rarity]}
                        usedIn={USED_IN[item.id] ?? []}
                        isLocked={isLocked}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {/* Discoverable T3 */}
            {filterItems(discoverableT3, true).length > 0 && (
              <section style={{ marginBottom: 18 }}>
                <SectionHeader
                  label="EXOTIC SALVAGE — T3"
                  color="#aa88ff"
                  count={discoverableT3.filter(i => discovered.has(i.id) || inventory[i.id] > 0).length}
                  total={discoverableT3.length}
                />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                  {filterItems(discoverableT3, true).map(item => {
                    const isLocked = !discovered.has(item.id) && !(inventory[item.id] > 0);
                    return (
                      <ItemCard
                        key={item.id}
                        item={item}
                        qty={inventory[item.id] ?? 0}
                        badgeLabel={isLocked ? "LOCKED" : RARITY_LABEL[item.rarity]}
                        badgeColor={isLocked ? "rgba(255,255,255,0.3)" : RARITY_COLOR[item.rarity]}
                        usedIn={USED_IN[item.id] ?? []}
                        isLocked={isLocked}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {/* Drop-only */}
            {filterItems(dropOnly, true).length > 0 && (
              <section style={{ marginBottom: 18 }}>
                <SectionHeader
                  label="DROP-ONLY SALVAGE"
                  color="#ffcc44"
                  count={dropOnly.filter(i => inventory[i.id] > 0).length}
                  total={dropOnly.length}
                />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                  {filterItems(dropOnly, true).map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      qty={inventory[item.id] ?? 0}
                      badgeLabel={RARITY_LABEL[item.rarity]}
                      badgeColor={RARITY_COLOR[item.rarity]}
                      usedIn={USED_IN[item.id] ?? []}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* ───── REFINED MATERIALS ───── */}
        {showRefined && (
          <>
            {[
              { items: t1mats, tier: 1 },
              { items: t2mats, tier: 2 },
              { items: t3mats, tier: 3 },
            ].map(({ items, tier }) => {
              const filtered = filterItems(items, false);
              if (filtered.length === 0) return null;
              return (
                <section key={tier} style={{ marginBottom: 18 }}>
                  <SectionHeader
                    label={`REFINED MATERIALS — ${TIER_LABEL[tier]}`}
                    color={TIER_COLOR[tier]}
                    count={items.filter(m => materials[m.id] > 0).length}
                    total={items.length}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                    {filtered.map(item => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        qty={materials[item.id] ?? 0}
                        badgeLabel={`${TIER_LABEL[tier]} REFINED`}
                        badgeColor={TIER_COLOR[tier]}
                        usedIn={USED_IN[item.id] ?? []}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}

        {/* Empty state */}
        {filter === "OWNED" && totalSalvageOwned + totalMatOwned === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(255,255,255,0.25)", fontSize: 11 }}>
            No items in cargo hold
          </div>
        )}
      </div>
    </div>
  );
}
